import { useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { FlowArrow, Phone, ListBullets, ArrowsClockwise, FloppyDisk, Check } from "@phosphor-icons/react";

// Simple visual IVR designer — fixed nodes, editable labels
const DEFAULT_FLOW = [
  { id: "start", type: "trigger", title: "Incoming call", label: "Toll-free: +1 (888) 555-0199", icon: Phone },
  { id: "greet", type: "play", title: "Play greeting", label: "Thanks for calling Hello by Flow. Press 1 for Sales, 2 for Support.", icon: ListBullets },
  { id: "branch", type: "branch", title: "Menu input", label: "Wait for keypress (1–2)", icon: ArrowsClockwise },
];

const DEFAULT_BRANCHES = [
  { id: "sales", key: "1", title: "Route to Sales", label: "Round-robin: Maya, Leo", target: "Sales team" },
  { id: "support", key: "2", title: "Route to Support", label: "Queue → Voicemail if > 60s", target: "Support team" },
];

export default function IVRBuilder() {
  const [name, setName] = useState("Main Flow");
  const [saved, setSaved] = useState(false);

  const save = async () => {
    await api.post("/ivr", { name, nodes: DEFAULT_FLOW, edges: DEFAULT_BRANCHES });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div className="p-8" data-testid="ivr-page">
      <PageHeader
        eyebrow="CALL FLOWS"
        title="IVR Builder"
        subtitle="Design your auto-attendant. Drag-and-drop coming soon — preview the flow below."
        right={
          <button onClick={save} className="btn-primary flex items-center gap-2" data-testid="ivr-save-btn">
            {saved ? <><Check size={16} weight="bold" /> Saved</> : <><FloppyDisk size={16} /> Save flow</>}
          </button>
        }
      />

      <div className="mt-6 max-w-md">
        <label className="label">Flow name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="ivr-name-input" />
      </div>

      {/* Flow canvas */}
      <div className="mt-8 relative card p-10 bg-[var(--bg-sidebar)]" data-testid="ivr-canvas">
        <div className="absolute inset-0 dot-grid opacity-40 pointer-events-none" />
        <div className="relative flex flex-col items-center gap-6">
          {DEFAULT_FLOW.map((n, i) => (
            <div key={n.id} className="flex flex-col items-center">
              <Node {...n} />
              {i < DEFAULT_FLOW.length - 1 && <div className="w-px h-8 bg-[var(--text-muted)]" />}
            </div>
          ))}

          {/* Branches */}
          <div className="grid grid-cols-2 gap-12 w-full max-w-2xl mt-2">
            {DEFAULT_BRANCHES.map((b) => (
              <div key={b.id} className="flex flex-col items-center" data-testid={`ivr-branch-${b.id}`}>
                <div className="font-mono text-[12px] font-bold bg-[var(--accent)] text-white px-3 py-1 mb-2">PRESS {b.key}</div>
                <div className="w-px h-6 bg-[var(--text-muted)]" />
                <Node id={b.id} type="route" title={b.title} label={b.label} icon={FlowArrow} target={b.target} />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function Node({ id, type, title, label, icon: Icon, target }) {
  const colors = {
    trigger: "border-[var(--accent)] bg-white",
    play: "border-[var(--border)] bg-white",
    branch: "border-[var(--warning)] bg-white",
    route: "border-[var(--success)] bg-white",
  };
  return (
    <div className={`relative border ${colors[type] || "border-[var(--border)] bg-white"} p-4 min-w-[280px]`} data-testid={`ivr-node-${id}`}>
      <div className="flex items-center gap-2 font-mono text-[10px] tracking-widest text-[var(--text-muted)]">
        <Icon size={12} /> {type.toUpperCase()}
      </div>
      <div className="font-display font-bold text-[15px] mt-1.5">{title}</div>
      <div className="text-[12px] text-[var(--text-secondary)] mt-1">{label}</div>
      {target && <div className="font-mono text-[11px] mt-2 text-[var(--accent)]">→ {target}</div>}
    </div>
  );
}
